import { Subscription } from 'rxjs';
import {
	REFERENCE_TARGET_REBUILT_COMMIT_REASON,
} from '../model/SketchEntityReferencePropagation';
import type { AssemblyTransformHistoryRecord } from '../../../assembly/component-instance';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import type { RebuildSketchEntityReferencesResult } from '../model/SketchEntityReferenceResults';
import type {
	SketchEntityReferencePendingRebuildTask,
	SketchEntityReferenceRebuildCoordinatorDependencies,
	SketchEntityReferenceSourceEntitiesRebuildTask,
	SketchEntityReferenceTopologyOwnerRebuildInput,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildCoordinator';

export type {
	SketchEntityReferenceRebuildCoordinatorDependencies,
	SketchEntityReferenceTopologyOwnerRebuildInput,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildCoordinator';

/**
 * 创建空的引用重建结果。
 *
 * @param diagnostics 本次重建需要携带的诊断信息。
 * @returns 标准重建结果。
 */
function createEmptyRebuildResult(
	diagnostics: ExecutionDiagnostic[] = [],
): RebuildSketchEntityReferencesResult {
	return {
		keptReferenceIds: [],
		removedReferenceIds: [],
		failedReferenceIds: [],
		diagnostics,
	};
}

/**
 * 草图实体引用重建协调器。
 * 说明：
 * 1. 普通草图 committed 由 FeatureRebuildOrchestrator 在普通 replay 与阶段 B 之间互斥路由；
 * 2. 本类只继续承接阶段 B 之外产生的目标引用变更，以及装配体 transform；
 * 3. 真正的投影重算、same-kind / kind-changed 分发均下沉到 rebuild service；
 * 4. 构造函数只保存依赖，由 `activate()` 按需建立文档级监听；
 * 5. 这样宿主只管理激活和释放，不承载任何重建业务逻辑。
 */
export class SketchEntityReferenceRebuildCoordinator {
	/** 自动重建事件订阅；未激活时保持为空。 */
	private subscription: Subscription | null = null;
	private readonly pendingQueueKeys: string[] = [];
	/**
	 * 以去重键保存完整任务对象。
	 *
	 * 队列键只负责定位和合并，不再编码任务类型或业务载荷。
	 */
	private readonly pendingTasksByKey = new Map<string, SketchEntityReferencePendingRebuildTask>();
	private isDrainingQueue = false;
	/** 带文档代次门禁的显式任务序号；此类任务禁止与其它代次合并等待者。 */
	private guardedTopologyTaskSequence = 0;
	/** 由时间线执行器显式接管下游顺序的传播轮次。 */
	private readonly suppressedAutomaticPropagationIds = new Set<string>();
	/** 防止释放后重新订阅事件或继续执行重建任务。 */
	private disposed = false;

	/**
	 * @param dependencies 协调器依赖。
	 */
	public constructor(
		private readonly dependencies: SketchEntityReferenceRebuildCoordinatorDependencies,
	) {}

	/**
	 * 激活当前文档的自动引用重建监听。
	 *
	 * 重复调用保持幂等；显式 topology owner 重建不依赖激活状态。
	 */
	public activate(): void {
		this.assertUsable();
		if (this.subscription) {
			return;
		}

		const subscription = new Subscription();
		try {
			this.bindAutomaticRebuildEvents(subscription);
			this.subscription = subscription;
		} catch (error) {
			subscription.unsubscribe();
			throw error;
		}
	}

	/**
	 * 释放订阅资源。
	 */
	public dispose(): void {
		if (this.disposed) {
			return;
		}

		this.disposed = true;
		this.subscription?.unsubscribe();
		this.subscription = null;
		this.pendingTasksByKey.forEach((task) => {
			if (task.kind !== 'topology-owners') {
				return;
			}
			task.waiters.forEach((waiter) => {
				waiter.reject(new Error('转换实体引用重建协调器已释放，排队中的 topology 重建已取消。'));
			});
		});
		this.pendingQueueKeys.length = 0;
		this.pendingTasksByKey.clear();
		this.suppressedAutomaticPropagationIds.clear();
		this.isDrainingQueue = false;
	}

	/**
	 * 在一次显式时间线 checkpoint 内暂停同轮自动链式传播。
	 *
	 * 阶段 B 已按历史位置显式安排后续草图 checkpoint；若 committed 监听再次入队，
	 * 同一引用链会并行执行两遍。该抑制只绑定当前 propagationId，并在工作结束后释放。
	 *
	 * @param propagationId 当前阶段 B 执行轮次 ID。
	 * @param work 需要保持显式顺序的 checkpoint 工作。
	 * @returns 工作结果。
	 */
	public async runWithAutomaticPropagationSuppressed<T>(
		propagationId: string,
		work: () => Promise<T>,
	): Promise<T> {
		this.assertUsable();
		this.suppressedAutomaticPropagationIds.add(propagationId);
		try {
			return await work();
		} finally {
			this.suppressedAutomaticPropagationIds.delete(propagationId);
		}
	}

	/**
	 * 绑定外部引用传播提交与装配变换两类自动重建事件。
	 *
	 * @param subscription 当前激活周期的订阅容器。
	 */
	private bindAutomaticRebuildEvents(subscription: Subscription): void {
		const definitionChangeHub = this.dependencies.getDefinitionChangeHub();
		if (definitionChangeHub) {
			subscription.add(
				definitionChangeHub.observeCommitted().subscribe((event) => {
					if (event.reason !== REFERENCE_TARGET_REBUILT_COMMIT_REASON) {
						return;
					}
					if (
						event.propagationId
						&& this.suppressedAutomaticPropagationIds.has(event.propagationId)
					) {
						return;
					}
					this.enqueueSourceEntityRebuild({
						sourceSketchId: event.persistentSketchId,
						sourceEntityIds: event.changedObjectIds ?? [],
						propagationId: event.propagationId,
						visitedReferenceIds: event.originReferenceIds ?? [],
					});
				}),
			);
		}

		const assemblyTransformCommitted$ = this.dependencies.observeAssemblyTransformCommitted?.();
		if (assemblyTransformCommitted$) {
			subscription.add(
				assemblyTransformCommitted$.subscribe((record) => {
					void this.rebuildTopologyOwnersByAssemblyTransform(record);
				}),
			);
		}
	}

	/**
	 * 按装配体 transform 提交记录触发 topology owner 级重建。
	 *
	 * @param record 装配体 transform 历史记录。
	 */
	private async rebuildTopologyOwnersByAssemblyTransform(
		record: AssemblyTransformHistoryRecord,
	): Promise<void> {
		const resolveOwners = this.dependencies.resolveTopologyOwnersByAssemblyTransform;
		if (!resolveOwners) {
			return;
		}

		try {
			const owners = await resolveOwners(record);
			if (owners.length <= 0) {
				return;
			}

			await this.rebuildTopologyEdgeReferencesForCurrentOwners({
				owners: [...owners],
				trigger: 'owner-transform-changed',
			});
		} catch (error) {
			console.error('装配体 transform 触发转换实体 topology 引用重建失败', {
				record,
			}, error);
		}
	}

	/**
	 * 将来源实体集合放入待重建队列。
	 * 说明：
	 * 1. 引用目标重建提交只影响 changedObjectIds 对应实体；
	 * 2. 这里按实体集合和 visited 集合去重，避免链式传播中重复排队；
	 * 3. visitedReferenceIds 会继续传给 rebuild service，用于跳过本轮已经处理过的引用。
	 *
	 * @param input 来源实体级重建载荷。
	 */
	private enqueueSourceEntityRebuild(
		input: Omit<SketchEntityReferenceSourceEntitiesRebuildTask, 'kind' | 'key'>,
	): void {
		if (this.disposed) {
			return;
		}

		if (input.sourceEntityIds.length <= 0) {
			return;
		}

		const sourceEntityIds = [...new Set(input.sourceEntityIds)].sort();
		const visitedReferenceIds = [...new Set(input.visitedReferenceIds)].sort();
		const queueKey = `entities:${input.sourceSketchId}:${sourceEntityIds.join(',')}:${
			input.propagationId ?? ''
		}`;
		const existingTask = this.pendingTasksByKey.get(queueKey);
		if (existingTask?.kind === 'source-entities') {
			existingTask.visitedReferenceIds = [
				...new Set([...existingTask.visitedReferenceIds, ...visitedReferenceIds]),
			].sort();
			return;
		}

		this.enqueueRebuildTask({
			kind: 'source-entities',
			key: queueKey,
			sourceSketchId: input.sourceSketchId,
			sourceEntityIds,
			propagationId: input.propagationId,
			visitedReferenceIds,
		});
	}

	/**
	 * 将完整重建任务放入统一串行队列。
	 *
	 * @param task 待执行任务。
	 */
	private enqueueRebuildTask(task: SketchEntityReferencePendingRebuildTask): void {
		if (this.disposed || this.pendingTasksByKey.has(task.key)) {
			return;
		}

		this.pendingTasksByKey.set(task.key, task);
		this.pendingQueueKeys.push(task.key);
		void this.drainRebuildQueue();
	}

	/**
	 * 直接按当前 topology owner 重建 topology-edge 引用。
	 *
	 * 设计说明：
	 * 1. topology 重建与草图重建共享同一个串行队列，禁止并发改写同一引用聚合；
	 * 2. rebuild 自身提交是否再次传播由 committed reason 过滤，不依赖绕开队列；
	 * 3. 显式调用等待自己的队列任务完成，并取得完整重建结果。
	 *
	 * @param input topology owner 级重建输入。
	 */
	public async rebuildTopologyEdgeReferencesForCurrentOwners(
		input: SketchEntityReferenceTopologyOwnerRebuildInput,
	): Promise<RebuildSketchEntityReferencesResult> {
		this.assertUsable();
		if (input.owners.length <= 0) {
			return createEmptyRebuildResult();
		}

		const stableInput: SketchEntityReferenceTopologyOwnerRebuildInput = {
			trigger: input.trigger,
			owners: input.owners.map((owner) => ({ ...owner })),
			isInputCurrent: input.isInputCurrent,
		};
		const queueKey = this.createTopologyRebuildTaskKey(stableInput);
		return new Promise<RebuildSketchEntityReferencesResult>((resolve, reject) => {
			const existingTask = this.pendingTasksByKey.get(queueKey);
			if (existingTask?.kind === 'topology-owners') {
				existingTask.waiters.push({ resolve, reject });
				return;
			}

			this.enqueueRebuildTask({
				kind: 'topology-owners',
				key: queueKey,
				input: stableInput,
				waiters: [{ resolve, reject }],
			});
		});
	}

	/**
	 * 执行已经从统一队列取出的 topology owner 重建任务。
	 *
	 * @param input topology owner 重建输入。
	 * @returns 标准重建结果。
	 */
	private async executeTopologyOwnerRebuild(
		input: SketchEntityReferenceTopologyOwnerRebuildInput,
	): Promise<RebuildSketchEntityReferencesResult> {
		try {
			return await this.dependencies.rebuildService.rebuildByTopologyOwners(input);
		} catch (error) {
			console.error('topology owner 触发转换实体引用重建失败', {
				trigger: input.trigger,
				owners: input.owners,
			}, error);
			return createEmptyRebuildResult([
				createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'error',
					message: `topology owner 触发转换实体引用重建失败: ${error instanceof Error ? error.message : String(error)}`,
				}),
			]);
		}
	}

	/**
	 * 串行消费待重建队列。
	 */
	private async drainRebuildQueue(): Promise<void> {
		if (this.isDrainingQueue) {
			return;
		}

		this.isDrainingQueue = true;
		try {
			while (this.pendingQueueKeys.length > 0 && !this.disposed) {
				const queuedKey = this.pendingQueueKeys.shift();
				if (!queuedKey) {
					continue;
				}

				const task = this.pendingTasksByKey.get(queuedKey);
				this.pendingTasksByKey.delete(queuedKey);
				if (!task) {
					continue;
				}

				try {
					switch (task.kind) {
						case 'source-entities':
							await this.dependencies.rebuildService.rebuildBySourceEntities({
								sourceSketchId: task.sourceSketchId,
								sourceEntityIds: task.sourceEntityIds,
								propagationId: task.propagationId,
								visitedReferenceIds: task.visitedReferenceIds,
							});
							break;
						case 'topology-owners': {
							const result = await this.executeTopologyOwnerRebuild(task.input);
							task.waiters.forEach((waiter) => {
								waiter.resolve(result);
							});
							break;
						}
					}
				} catch (error) {
					if (task.kind === 'topology-owners') {
						task.waiters.forEach((waiter) => {
							waiter.reject(error);
						});
						continue;
					}
					console.error(`草图实体引用自动重建失败: ${task.key}`, error);
				}
			}
		} finally {
			this.isDrainingQueue = false;
			if (this.pendingQueueKeys.length > 0 && !this.disposed) {
				void this.drainRebuildQueue();
			}
		}
	}

	/**
	 * 创建 topology owner 任务的稳定去重键。
	 *
	 * @param input topology owner 重建输入。
	 * @returns 仅用于队列定位的稳定键。
	 */
	private createTopologyRebuildTaskKey(
		input: SketchEntityReferenceTopologyOwnerRebuildInput,
	): string {
		const ownerKeys = input.owners.map((owner) => ([
			owner.ownerKind,
			owner.ownerId,
			owner.bodyBindingId ?? '',
			owner.matchAnyBodyBinding === true ? 'all' : 'single',
		].join(':'))).sort();
		const stableOwnerKey = `topology:${input.trigger}:${ownerKeys.join('|')}`;
		if (!input.isInputCurrent) {
			return stableOwnerKey;
		}
		/** 文档恢复任务携带各自代次闭包，不能与早先任务共享执行结果。 */
		this.guardedTopologyTaskSequence += 1;
		return `${stableOwnerKey}:guarded:${this.guardedTopologyTaskSequence}`;
	}

	/**
	 * 阻止已经释放的协调器重新订阅事件或执行显式重建。
	 */
	private assertUsable(): void {
		if (this.disposed) {
			throw new Error('转换实体引用重建协调器已释放，不能继续使用');
		}
	}
}
